import { z } from "zod";
/**
 * The password policy (plan de seguridad, Fase 1), the same on both sides of the wire. The list
 * of common passwords lives here, in the code, on purpose: nothing about it is configurable from
 * the platform (Martín, 2026-10-20). Only passwords being chosen are checked; login never is.
 */
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 128;
/**
 * Common passwords of 10 characters or more — the leaked-password top lists, filtered to the
 * length the policy already demands — plus obvious Spanish and Argentine variants and words a
 * studio's staff would reach for. Compared after normalising (lowercase, no accents, no spaces).
 */
const COMMON = [
    "1234567890",
    "0123456789",
    "0987654321",
    "1111111111",
    "0000000000",
    "1122334455",
    "1234512345",
    "1234554321",
    "1231231231",
    "1212121212",
    "9876543210",
    "12345678910",
    "123456789a",
    "a123456789",
    "123456789q",
    "q123456789",
    "1q2w3e4r5t",
    "1q2w3e4r5t6y",
    "q1w2e3r4t5",
    "1qaz2wsx3edc",
    "qwertyuiop",
    "qwerty1234",
    "qwerty12345",
    "qwerty123456",
    "asdfghjkl",
    "asdfghjkl1",
    "asdfghjklñ",
    "zxcvbnm123",
    "qazwsxedc1",
    "qazwsxedcrfv",
    "password12",
    "password123",
    "password1234",
    "passw0rd123",
    "p4ssw0rd123",
    "password!",
    "iloveyou12",
    "iloveyou123",
    "princess12",
    "sunshine12",
    "football12",
    "baseball12",
    "superman12",
    "batman1234",
    "starwars12",
    "computer12",
    "welcome123",
    "welcome1234",
    "letmein123",
    "trustno1234",
    "monkey1234",
    "dragon1234",
    "master1234",
    "shadow1234",
    "abcdefghij",
    "abcdefghijk",
    "abc1234567",
    "abcd123456",
    "abcde12345",
    "aaaaaaaaaa",
    "contrasena",
    "contrasena1",
    "contrasena12",
    "contrasena123",
    "contrasena1234",
    "micontrasena",
    "nuevacontrasena",
    "contrasenasegura",
    "clavesegura",
    "clavesegura1",
    "miclave1234",
    "laclave1234",
    "clave123456",
    "clave1234567",
    "nuevaclave1",
    "nuevaclave123",
    "tuclave1234",
    "minuevaclave",
    "claveclave",
    "123456clave",
    "cambiar1234",
    "cambiame123",
    "cambiarclave",
    "cambiame1234",
    "temporal123",
    "temporal1234",
    "provisoria1",
    "provisoria123",
    "bienvenido1",
    "bienvenido12",
    "bienvenido123",
    "bienvenida1",
    "bienvenida123",
    "teamo12345",
    "teamo123456",
    "teamomucho",
    "tequiero123",
    "estrella123",
    "mariposa123",
    "argentina1",
    "argentina12",
    "argentina123",
    "argentina1234",
    "argentina2022",
    "argentina2026",
    "buenosaires",
    "buenosaires1",
    "buenosaires123",
    "riverplate",
    "riverplate1",
    "riverplate123",
    "bocajuniors",
    "bocajuniors1",
    "bocajuniors123",
    "racingclub1",
    "sanlorenzo1",
    "independiente",
    "independiente1",
    "messi10messi",
    "leomessi10",
    "maradona10",
    "maradona1010",
    "lionelmessi",
    "lionelmessi10",
    "futbol12345",
    "mundial2022",
    "campeones2022",
    "campeondelmundo",
    "estudio1234",
    "estudio12345",
    "estudiocontable",
    "estudiocontable1",
    "contador123",
    "contador1234",
    "contadora123",
    "contabilidad",
    "contabilidad1",
    "contabilidad123",
    "impuestos123",
    "afip123456",
    "afip1234567",
    "arca123456",
    "arca1234567",
    "monotributo",
    "monotributo1",
    "facturacion",
    "facturacion1",
    "sueldos1234",
    "oficina1234",
    "oficina12345",
    "empresa1234",
    "empresa12345",
    "administracion",
    "administrador",
    "administrador1",
    "admin123456",
    "admin1234567",
    "administrator",
    "estudiobaldi",
    "estudiobaldi1",
    "estudiobaldi123",
    "baldi12345",
    "baldi123456",
    "baldiferreyra",
    "baldiyferreyra",
    "mercadocentral",
    "mercadocentral1",
    "devengo123",
    "devengo1234",
    "febrero2026",
    "octubre2026",
    "noviembre2026",
    "diciembre2026",
    "invierno2026",
    "primavera2026",
].map(normalize);
const COMMON_SET = new Set(COMMON);
function normalize(value) {
    return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, "");
}
/** Everything that is not a letter or digit splits a name or address into tokens. */
function tokens(value) {
    return normalize(value)
        .split(/[^\p{L}\p{N}]+/u)
        .filter((t) => t.length >= 4);
}
/** One or two repeated characters, or a run up or down (digits wrap: «8901234567»). */
function isMonotonous(value) {
    if (new Set(value).size <= 2)
        return true;
    const codes = [...value].map((c) => c.codePointAt(0) ?? 0);
    const digits = /^\d+$/.test(value);
    const steps = new Set(codes.slice(1).map((c, i) => {
        const step = c - (codes[i] ?? 0);
        return digits ? (step + 10) % 10 : step;
    }));
    if (steps.size !== 1)
        return false;
    const [step] = [...steps];
    return digits ? step === 1 || step === 9 : step === 1 || step === -1;
}
/**
 * Why a new password is not acceptable, in Spanish, or `null` when it is. `email` and `name` are
 * the person's: a password made of them is the first thing an attacker tries.
 */
export function passwordProblem(password, person = {}) {
    if (password.length < PASSWORD_MIN) {
        return `La clave tiene que tener al menos ${PASSWORD_MIN} caracteres.`;
    }
    if (password.length > PASSWORD_MAX) {
        return `La clave puede tener como máximo ${PASSWORD_MAX} caracteres.`;
    }
    const plain = normalize(password);
    if (COMMON_SET.has(plain) || COMMON_SET.has(plain.replace(/[^\p{L}\p{N}]+$/u, ""))) {
        return "Esa clave es de las más usadas y se adivina fácil. Elegí otra.";
    }
    if (isMonotonous(plain)) {
        return "La clave no puede ser una serie (como 1234567890 o aaaaaaaaaa). Elegí otra.";
    }
    const own = [...tokens((person.email ?? "").split("@")[0] ?? ""), ...tokens(person.name ?? "")];
    if (own.some((t) => plain.includes(t))) {
        return "La clave no puede contener tu nombre ni tu correo.";
    }
    return null;
}
/** A password being chosen: length bounds here; the full check needs the person (above). */
export const newPasswordSchema = z
    .string()
    .min(PASSWORD_MIN, `La clave tiene que tener al menos ${PASSWORD_MIN} caracteres.`)
    .max(PASSWORD_MAX, `La clave puede tener como máximo ${PASSWORD_MAX} caracteres.`);
//# sourceMappingURL=password-policy.js.map